import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { DateTime } from 'luxon';
import { PaginatedResponse, Patient, Provider, User } from '../../react/api/model';
import { UUID } from '../../react/types';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userTimezone = DateTime.now().zoneName;

  constructor(private httpClient: HttpClient) {}

  public async getProviderUserById(providerId: number): Promise<Provider> {
    const url = environment.niceServiceUrl.concat('/v3/users');
    const params = new HttpParams().append('providerId', providerId);
    const response = await this.httpClient.get<PaginatedResponse<User>>(url, { params }).toPromise();
    const providers = response.content.map((user: User, index: number) => {
      if (user.type.startsWith('FindUserResponse::Provider')) {
        return response.content[index] as Provider;
      } else {
        throw new Error('Invalid user type: ' + user.type + ' for user: ' + user.userId);
      }
    });

    if (providers.length > 1) {
      throw new Error('Invalid provider list for provider ID: ' + providerId);
    } else if (providers.length === 0) {
      return null;
    } else {
      return providers[0];
    }
  }

  public async getProviderUserByUserId(userId: UUID): Promise<Provider> {
    const url = environment.niceServiceUrl.concat('/v3/users');
    const params = new HttpParams().append('userId', userId);
    const response = await this.httpClient.get<PaginatedResponse<User>>(url, { params }).toPromise();
    const providers = response.content.map((user: User, index: number) => {
      if (user.type.startsWith('FindUserResponse::Provider')) {
        return response.content[index] as Provider;
      } else {
        throw new Error('Invalid user type: ' + user.type + ' for user: ' + user.userId);
      }
    });

    if (providers.length > 1) {
      throw new Error('Invalid provider list for provider user ID: ' + userId);
    } else if (providers.length === 0) {
      return null;
    } else {
      return providers[0];
    }
  }

  public async getProviderUserByAuth0Id(auth0Id: string): Promise<Provider> {
    const url = environment.niceServiceUrl.concat('/v3/users');
    const params = new HttpParams().append('auth0Id', auth0Id);
    const response = await this.httpClient.get<PaginatedResponse<User>>(url, { params }).toPromise();
    const providers = response.content.map((user: User, index: number) => {
      if (user.type.startsWith('FindUserResponse::Provider')) {
        return response.content[index] as Provider;
      } else {
        throw new Error('Invalid user type: ' + user.type + ' for user: ' + user.userId);
      }
    });

    if (providers.length > 1) {
      throw new Error('Invalid provider list for provider Auth0 ID: ' + auth0Id);
    } else if (providers.length === 0) {
      return null;
    } else {
      return providers[0];
    }
  }

  /**
   * Return the user search results for a list of patients.
   */
  public async getPatients(patientIds: number[]): Promise<Patient[]> {
    const patients: Patient[] = [];

    for (let i = 0; i < patientIds.length; i++) {
      patients.push((await this.getPaginatedUsers(patientIds[i]).toPromise()).content[0] as Patient);
    }

    return patients;
  }

  public async getPatientUsers(patientIds: number[]): Promise<Patient[]> {
    const patients: Patient[] = [];

    for (let i = 0; i < patientIds.length; i++) {
      const patient = await this.getPatientUser(patientIds[i]);
      if (patient) {
        patients.push(patient);
      }
    }

    return patients;
  }

  public async getPatientUser(patientId: number): Promise<Patient | null> {
    const url = environment.niceServiceUrl.concat('/v3/users');
    const params = new HttpParams().append('patientId', patientId);
    const response = await this.httpClient.get<PaginatedResponse<User>>(url, { params }).toPromise();

    const patients: Patient[] = response.content.map((user: User, index: number) => {
      if (user.type.startsWith('FindUserResponse::Patient')) {
        return response.content[index] as Patient;
      } else {
        throw new Error('Invalid user type: ' + user.type + ' for user: ' + user.userId);
      }
    });

    if (patients.length > 1) {
      throw new Error('Invalid patient list for patient ID: ' + patientId);
    } else if (patients.length === 0) {
      return null;
    } else {
      return patients[0];
    }
  }

  public async getPatientUserByUserId(userId: UUID): Promise<Patient> {
    const url = environment.niceServiceUrl.concat('/v3/users');
    const params = new HttpParams().append('userId', userId);
    const response = await this.httpClient.get<PaginatedResponse<User>>(url, { params }).toPromise();

    const patients = response.content.map((user: User, index: number) => {
      if (user.type.startsWith('FindUserResponse::Patient')) {
        return response.content[index] as Patient;
      } else {
        throw new Error('Invalid user type: ' + user.type + ' for user: ' + user.userId);
      }
    });

    if (patients.length > 1) {
      throw new Error('Invalid patient list for patient user ID: ' + userId);
    } else if (patients.length === 0) {
      return null;
    } else {
      return patients[0];
    }
  }

  public async getPatientUserByElationId(elationId: string): Promise<Patient> {
    const url = environment.niceServiceUrl.concat('/v3/users');
    const params = new HttpParams().append('elationId', elationId);

    const response = await this.httpClient.get<PaginatedResponse<User>>(url, { params }).toPromise();

    const patients = response.content.map((user: User, index: number) => {
      if (user.type.startsWith('FindUserResponse::Patient')) {
        return response.content[index] as Patient;
      } else {
        throw new Error('Invalid user type: ' + user.type + ' for user: ' + user.userId);
      }
    });

    if (patients.length > 1) {
      throw new Error('Invalid patient list for Elation ID: ' + elationId);
    } else if (patients.length === 0) {
      return null;
    } else {
      return patients[0];
    }
  }

  private getPaginatedUsers(
    patientId: number,
    page: number = 0,
    order?: 'ASC' | 'DESC',
    pageSize?: number
  ): Observable<PaginatedResponse<Patient | Provider>> {
    const queryParams = new HttpParams({
      fromObject: {
        type: patientId && patientId !== 0 ? 'PATIENT' : 'PROVIDER',
        patientId: patientId ? patientId : undefined,
        page: page.toString(),
        size: pageSize ? pageSize : 30,
        // sortBy: 'START_DATE',
        order: order ? order : 'ASC',
      },
    });

    const url = environment.niceServiceUrl.concat('/v3/users');
    return this.httpClient
      .get<any>(url, {
        params: queryParams,
      })
      .pipe(
        take(1),
        map((response: PaginatedResponse<Patient | Provider>) => {
          return response;
        })
      );
  }
}
